import React, { useEffect, useRef, useState } from "react";

declare global {
  interface Window { google: any; }
}

const API_URL          = process.env.REACT_APP_API_URL || "http://localhost:8000";
const GOOGLE_CHARTS_URL = "https://www.gstatic.com/charts/loader.js";

type SideFilter = "all" | "paternal" | "maternal" | "unknown";
type ViewMode   = "group" | "chromosome" | "progenitor";

const FILTER_OPTIONS: { value: SideFilter; label: string }[] = [
  { value: "all",      label: "All"      },
  { value: "paternal", label: "Paternal" },
  { value: "maternal", label: "Maternal" },
  { value: "unknown",  label: "Unknown"  },
];

const FILTER_COLOURS: Record<SideFilter, string> = {
  all:      "#1976d2",
  paternal: "#1565c0",
  maternal: "#6a1b9a",
  unknown:  "#546e7a",
};

// ── Google Charts loader ────────────────────────────────────────────────────
function loadGoogleCharts() {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.charts) { resolve(undefined); return; }
    const existing = document.querySelector(`script[src="${GOOGLE_CHARTS_URL}"]`);
    if (existing) {
      const poll = setInterval(() => {
        if (window.google && window.google.charts) { clearInterval(poll); resolve(undefined); }
      }, 50);
      return;
    }
    const script   = document.createElement("script");
    script.src     = GOOGLE_CHARTS_URL;
    script.onload  = () => resolve(undefined);
    script.onerror = () => reject(new Error("Failed to load Google Charts"));
    document.body.appendChild(script);
  });
}

let sankeyPackageLoaded = false;

// ── Helpers ────────────────────────────────────────────────────────────────
function pillBtn(active: boolean, colour: string, onClick: () => void, children: string) {
  return (
    <button
      key={children}
      onClick={onClick}
      style={{
        padding:    "7px 20px",
        borderRadius: 20,
        border:     `2px solid ${active ? colour : "#ddd"}`,
        background: active ? colour : "#fff",
        color:      active ? "#fff" : "#444",
        fontWeight: active ? 700 : 400,
        cursor:     "pointer",
        fontSize:   14,
        transition: "all 0.15s",
        boxShadow:  active ? `0 2px 8px ${colour}44` : "none",
      }}
    >
      {children}
    </button>
  );
}

// ── Component ──────────────────────────────────────────────────────────────
const DNASankey: React.FC = () => {
  const chartRef = useRef<HTMLDivElement>(null);

  const [status,        setStatus]       = useState<"loading"|"empty"|"error"|"ok">("loading");
  const [errorMsg,      setErrorMsg]     = useState("");
  const [sideFilter,    setSideFilter]   = useState<SideFilter>("all");
  const [viewMode,      setViewMode]     = useState<ViewMode>("group");
  const [matchCount,    setMatchCount]   = useState(0);
  const [minOverlapCm,  setMinOverlapCm] = useState(7);
  // Debounced value so we don't refetch on every keystroke
  const [appliedOverlap, setAppliedOverlap] = useState(7);

  // Apply overlap threshold with a small debounce
  useEffect(() => {
    const t = setTimeout(() => setAppliedOverlap(minOverlapCm), 600);
    return () => clearTimeout(t);
  }, [minOverlapCm]);

  useEffect(() => {
    let cancelled = false;

    async function drawChart() {
      setStatus("loading");
      try {
        // 1. Load Google Charts (once per page)
        await loadGoogleCharts();
        if (cancelled) return;

        if (!sankeyPackageLoaded) {
          await new Promise((resolve) => {
            window.google.charts.load("current", { packages: ["sankey"] });
            window.google.charts.setOnLoadCallback(() => resolve(undefined));
          });
          sankeyPackageLoaded = true;
        }
        if (cancelled) return;

        // 2. Build URL
        const params: string[] = [];
        if (sideFilter !== "all") params.push("side=" + sideFilter);
        if (viewMode === "chromosome") params.push("min_overlap_cm=" + appliedOverlap);
        const endpoint = viewMode === "chromosome"
          ? "dna_sankey_chromosomes"
          : viewMode === "progenitor"
            ? "dna_sankey_progenitors"
            : "dna_sankey";
        const url = `${API_URL}/${endpoint}${params.length ? "?" + params.join("&") : ""}`;

        const response = await fetch(url);
        if (!response.ok) throw new Error(`API error ${response.status}`);
        const rows = await response.json();
        if (cancelled) return;

        if (!rows || rows.length === 0) { setStatus("empty"); return; }

        // 3. Dynamic height — count rightmost (match) nodes
        const sources  = new Set(rows.map((r: any) => r[0]));
        const allNodes = Array.from(new Set(
          rows.reduce((acc: any[], r: any) => { acc.push(r[0]); acc.push(r[1]); return acc; }, [])
        ));
        const matchNodes = allNodes.filter((n: any) => !sources.has(n));
        const nodeCount  = matchNodes.length;
        const chartH     = Math.max(700, nodeCount * 30 + 140);
        if (!cancelled) setMatchCount(nodeCount);

        // 4. Build DataTable with HTML tooltip column
        const data = new window.google.visualization.DataTable();
        data.addColumn("string", "From");
        data.addColumn("string", "To");
        data.addColumn("number", "cM");
        data.addColumn({ type: "string", role: "tooltip", p: { html: true } });

        const nodeInflow: {[k: string]: number} = {};
        rows.forEach((r: any) => { nodeInflow[r[1]] = (nodeInflow[r[1]] || 0) + r[2]; });

        const tableRows = rows.map((r: any) => {
          const from   = r[0];
          const to     = r[1];
          const weight = r[2];
          const pct    = nodeInflow[to]
            ? " (" + Math.round((weight / nodeInflow[to]) * 100) + "% of total for " + to + ")"
            : "";
          const tip = "<div style='padding:8px 12px;font-family:sans-serif;font-size:13px;line-height:1.7'>"
            + "<b>" + from + "</b> \u2192 <b>" + to + "</b><br/>"
            + "<span style='color:#555'>Shared DNA: <b>" + weight.toFixed(1) + " cM</b></span>"
            + (pct ? "<br/><span style='color:#888;font-size:12px'>" + pct + "</span>" : "")
            + "</div>";
          return [from, to, weight, tip];
        });
        data.addRows(tableRows);

        // 5. Draw
        const accentColour = FILTER_COLOURS[sideFilter];
        const options = {
          tooltip: { isHtml: true },
          sankey: {
            link: { colorMode: "gradient", color: { fillOpacity: 0.5 } },
            node: {
              nodePadding: 24,
              width:       16,
              label:       { fontSize: 11, bold: false, color: "#333" },
              colors:      [accentColour],
              interactivity: true,
            },
          },
        };

        if (!chartRef.current) return;
        chartRef.current.style.height = `${chartH}px`;
        setStatus("ok");
        await new Promise(res => setTimeout(res, 0));
        if (cancelled) return;
        new window.google.visualization.Sankey(chartRef.current).draw(data, options);

      } catch (e) {
        if (!cancelled) {
          setErrorMsg((e as any)?.message || "Unknown error");
          setStatus("error");
        }
      }
    }

    drawChart();
    return () => { cancelled = true; };
  }, [sideFilter, viewMode, appliedOverlap]);

  const accentColour = FILTER_COLOURS[sideFilter];

  return (
    <div>

      {/* ── View mode toggle ───────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 0, marginBottom: 16, borderRadius: 8, overflow: "hidden", border: "1px solid #ddd", width: "fit-content" }}>
        {(["group", "chromosome", "progenitor"] as ViewMode[]).map(mode => {
          const active = viewMode === mode;
          const label  = mode === "group" ? "By Group"
                       : mode === "chromosome" ? "By Segment Clusters"
                       : "By Progenitor";
          return (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              style={{
                padding:    "8px 22px",
                border:     "none",
                background: active ? accentColour : "#f5f5f5",
                color:      active ? "#fff" : "#555",
                fontWeight: active ? 700 : 400,
                cursor:     "pointer",
                fontSize:   14,
                transition: "all 0.15s",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* ── Min-overlap control (chromosome mode only) ─────────────────── */}
      {viewMode === "chromosome" && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, fontSize: 13, color: "#555" }}>
          <span>Min. overlap to cluster:</span>
          <input
            type="number"
            min={1}
            max={50}
            step={0.5}
            value={minOverlapCm}
            onChange={e => setMinOverlapCm(parseFloat(e.target.value) || 1)}
            style={{ width: 64, padding: "4px 8px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13 }}
          />
          <span>cM</span>
          <span style={{ color: "#aaa", fontSize: 12 }}>(segments with ≥ this much shared overlap are merged into one cluster)</span>
        </div>
      )}

      {/* ── Side filter pills ──────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        {FILTER_OPTIONS.map(opt =>
          pillBtn(
            sideFilter === opt.value,
            FILTER_COLOURS[opt.value],
            () => setSideFilter(opt.value),
            opt.label
          )
        )}
        {status === "ok" && matchCount > 0 && (
          <span style={{ color: "#888", fontSize: 13, marginLeft: 8 }}>
            {matchCount} match{matchCount !== 1 ? "es" : ""}
          </span>
        )}
      </div>

      {/* ── Status messages ────────────────────────────────────────────── */}
      {status === "loading" && (
        <div style={{ color: "#888", padding: "24px 0" }}>Loading DNA flow data…</div>
      )}
      {status === "empty" && (
        <div style={{ color: "#888", padding: "24px 0" }}>
          {viewMode === "progenitor"
            ? "No progenitor flow data found. Make sure individuals in the tree have is_progenitor ticked and are linked to DNA Painter matches via the individual modal."
            : sideFilter === "all"
              ? "No DNA flow data available yet. Import a DNA Painter CSV on the Chromosome Painter page to populate this chart."
              : `No ${sideFilter} DNA flow data found${viewMode === "chromosome" ? " — try reducing the min. overlap threshold" : ""}. Try a different filter.`}
        </div>
      )}
      {status === "error" && (
        <div style={{ color: "#c62828", padding: "24px 0" }}>
          Failed to load DNA flow chart: {errorMsg}
        </div>
      )}

      {/* ── Chart ──────────────────────────────────────────────────────── */}
      <div style={{ overflowX: "auto", overflowY: "visible" }}>
        <div
          id="dna_sankey"
          ref={chartRef}
          style={{
            width:    "100%",
            minWidth: 700,
            height:   700,
            display:  (status === "empty" || status === "error") ? "none" : "block",
          }}
        />
      </div>

      {status === "ok" && (
        <p style={{ color: "#aaa", fontSize: 12, marginTop: 8 }}>
          Hover over a flow band to see shared cM details.
          {viewMode === "chromosome" && " Cluster labels show chromosome and position in Mb."}
          {viewMode === "progenitor" && " Flow shows: You \u2192 DNA Match \u2192 Progenitor. Width = total shared cM. Together the matches reconstruct the progenitor\u2019s DNA."}
        </p>
      )}
    </div>
  );
};

export default DNASankey;
